import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Bus, Eye, EyeOff, Loader2, XCircle } from "lucide-react";
// PROMPT 96 (2026-09-11): la pantalla a la que lleva el link del correo.
// Antes de mostrar el formulario se pregunta si el link sigue sirviendo, para avisar
// "venció" en vez de dejar escribir una contraseña que después no se va a poder guardar.
export default function RestablecerPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [estado, setEstado] = useState("verificando"); // verificando | valido | invalido
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repetir, setRepetir] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const { data } = await api.get(`/auth/recuperar/${token}`);
        if (!vivo) return;
        setEstado(data?.valido ? "valido" : "invalido");
        setEmail(data?.email || "");
      } catch {
        if (vivo) setEstado("invalido");
      }
    })();
    return () => { vivo = false; };
  }, [token]);
  const corta = password.length > 0 && password.length < 8;
  const noCoincide = repetir.length > 0 && password !== repetir;
  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("La contraseña debe tener al menos 8 caracteres.");
    if (password !== repetir) return setError("Las dos contraseñas no son iguales.");
    setGuardando(true);
    try {
      await api.post("/auth/restablecer", { token, password });
      toast.success("Listo. Ya podés entrar con la contraseña nueva.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No pudimos guardar la contraseña.");
    } finally {
      setGuardando(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-10">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white">
            <Bus className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-bold text-slate-900">Tours NY</div>
            <div className="text-xs text-slate-500 tracking-wide">· Reservas</div>
          </div>
        </div>
        {estado === "verificando" && (
          <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="restablecer-verificando">
            <Loader2 className="h-4 w-4 animate-spin" /> Revisando el link…
          </div>
        )}
        {estado === "invalido" && (
          <div data-testid="restablecer-invalido">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-50 text-amber-600 mb-4">
              <XCircle className="h-5 w-5" />
            </div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">
              Este link ya no sirve
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              Los links duran una hora y se usan una sola vez. Pedí uno nuevo y te llega al momento.
            </p>
            <Link to="/recuperar" className="inline-block mt-8">
              <Button data-testid="restablecer-pedir-otro">Pedir un link nuevo</Button>
            </Link>
          </div>
        )}
        {estado === "valido" && (
          <>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">
              Elegí tu contraseña nueva
            </h1>
            <p className="text-sm text-slate-500 mt-2 mb-8">
              {email ? `Para la cuenta ${email}.` : ""} Tiene que tener al menos 8 caracteres.
            </p>
            <form onSubmit={guardar} className="space-y-5" data-testid="restablecer-form">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700">Contraseña nueva</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPwd ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="restablecer-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {corta && <p className="text-xs text-amber-600">Le faltan caracteres.</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="repetir" className="text-slate-700">Repetila</Label>
                <Input
                  id="repetir"
                  type={showPwd ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={repetir}
                  onChange={(e) => setRepetir(e.target.value)}
                  required
                  data-testid="restablecer-repetir"
                />
                {noCoincide && <p className="text-xs text-amber-600">No son iguales.</p>}
              </div>
              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="restablecer-error">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={guardando || password.length < 8 || password !== repetir}
                data-testid="restablecer-guardar"
              >
                {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar y entrar"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
